import { useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Separator } from "@/frontend/components/ui/separator";
import { useSession } from "@/frontend/auth-client";
import {
  useUpsertClinicalWorkflowMutation,
  useDisableClinicalWorkflowMutation,
} from "@/frontend/mutations/dashboard/settings";
import { getProviderDocsUrl } from "@/frontend/utils/dashboard/settings/ehr-docs";
import { DocsHelpLink } from "./DocsHelpLink";
import { ProviderLogo } from "./ProviderLogo";
import { CustomCredentialsCard } from "./CustomCredentialsCard";
import { Switch } from "@/frontend/components/ui/switch";
import { CREDENTIAL_MODE } from "@/frontend/types/ehr-clients";
import type { CredentialMode, Provider, ClinicalWorkflowRow } from "@/frontend/types/ehr-clients";
import type { EhrClinicalWorkflowType } from "@/frontend/constants/ehr-providers";
import { isAbsoluteHttpUrl } from "@/frontend/utils/shared";

interface Props {
  provider: Provider;
  savedRow: ClinicalWorkflowRow | undefined;
}

/**
 * Detail view for a Clinical Workflows provider, a SMART on FHIR app launched
 * inside the EHR clinician workflow. It shows the org-scoped launch URL and
 * scopes to register in the EHR, and lets the org set the app launch URL and
 * custom credentials, then enable, save or disable the provider.
 */
export function EhrClinicalWorkflowDetailView({ provider, savedRow }: Props) {
  const { data: session } = useSession();
  const activeOrganizationId = session?.session?.activeOrganizationId ?? null;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const launchUrl = activeOrganizationId ? `${origin}/api/auth/smart/launch/${activeOrganizationId}` : null;
  const cdsHooksUrl = activeOrganizationId ? `${origin}/cds-hooks/${activeOrganizationId}` : null;
  const redirectUri = `${origin}/api/auth/smart/launch-callback`;
  const jwksUri = `${origin}/api/auth/.well-known/jwks`;

  const providerType = provider.type as EhrClinicalWorkflowType;
  const docsUrl = getProviderDocsUrl(provider.type);
  const upsert = useUpsertClinicalWorkflowMutation();
  const disable = useDisableClinicalWorkflowMutation();

  // Enabled-by-default: no row OR a row without disabled_at means enabled.
  const isConnectionEnabled = !savedRow?.disabled_at;
  const savedCredentialMode: CredentialMode = savedRow?.client_id ? CREDENTIAL_MODE.OWN : CREDENTIAL_MODE.MEDBLOCKS;
  const savedClientId = savedRow?.client_id ?? "";
  const savedAppLaunchUrl = savedRow?.app_launch_url ?? "";
  const savedCdsEndpoint = savedRow?.cds_endpoint ?? "";
  const savedCdsCachingEnabled = savedRow?.cds_caching_enabled ?? false;

  // Parent waits for both config queries before rendering this view, so these
  // initializers are stable — no sync effect needed.
  const [credentialMode, setCredentialMode] = useState<CredentialMode>(savedCredentialMode);
  const [customClientId, setCustomClientId] = useState(savedClientId);
  const [appLaunchUrl, setAppLaunchUrl] = useState(savedAppLaunchUrl);
  const [cdsEndpoint, setCdsEndpoint] = useState(savedCdsEndpoint);
  const [cdsCachingEnabled, setCdsCachingEnabled] = useState(savedCdsCachingEnabled);

  const trimmedAppLaunchUrl = appLaunchUrl.trim();
  const trimmedCdsEndpoint = cdsEndpoint.trim();
  const isAppLaunchUrlValid = trimmedAppLaunchUrl === "" || isAbsoluteHttpUrl(trimmedAppLaunchUrl);
  const isCdsEndpointValid = trimmedCdsEndpoint === "" || isAbsoluteHttpUrl(trimmedCdsEndpoint);
  const isClientIdRequired = credentialMode === CREDENTIAL_MODE.OWN && !customClientId.trim();

  const hasUnsavedChanges = (() => {
    if (!isConnectionEnabled) return false;
    if (trimmedAppLaunchUrl !== savedAppLaunchUrl) return true;
    if (trimmedCdsEndpoint !== savedCdsEndpoint) return true;
    if (cdsCachingEnabled !== savedCdsCachingEnabled) return true;
    if (credentialMode !== savedCredentialMode) return true;
    if (credentialMode === CREDENTIAL_MODE.OWN && customClientId.trim() !== savedClientId) return true;
    return false;
  })();

  const canSave = hasUnsavedChanges && isAppLaunchUrlValid && isCdsEndpointValid && !isClientIdRequired;
  const isMutationPending = upsert.isPending || disable.isPending;

  const connectionStatusText = !isConnectionEnabled
    ? "Not connected"
    : savedCredentialMode === CREDENTIAL_MODE.OWN
    ? "Using custom credentials"
    : "Using Medblocks' credentials";

  /** Build the upsert payload from current form state. */
  function buildPayload() {
    const clientId = credentialMode === CREDENTIAL_MODE.OWN ? customClientId.trim() || null : null;
    return {
      providerType,
      clientId,
      appLaunchUrl: trimmedAppLaunchUrl || null,
      cdsEndpoint: trimmedCdsEndpoint || null,
      cdsCachingEnabled,
    };
  }

  function handleSave() {
    if (!canSave) return;
    upsert.mutate(buildPayload());
  }

  function handleEnable() {
    // Reactivate preserving any previously-saved values.
    upsert.mutate({
      providerType,
      clientId: savedRow?.client_id ?? null,
      appLaunchUrl: savedRow?.app_launch_url ?? null,
      cdsEndpoint: savedRow?.cds_endpoint ?? null,
      cdsCachingEnabled: savedRow?.cds_caching_enabled ?? false,
    });
  }

  function handleDisable() {
    disable.mutate({ providerType });
  }

  return (
    <div className="flex flex-col gap-8">
      <Link
        to="/dashboard/settings"
        search={{ section: "clients", provider: "", destinationId: undefined }}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-4" />
        Back to Connections
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex size-10 items-center justify-center rounded-lg border bg-white p-1.5 shrink-0">
            <ProviderLogo provider={provider.type} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">{provider.label}</h2>
              <DocsHelpLink
                href={docsUrl}
                tooltip={provider.description}
                ariaLabel={`Open ${provider.label} integration guide`}
              />
              {isConnectionEnabled ? (
                <span className="inline-flex items-center rounded-md bg-green-50 border border-green-200 px-2 py-0.5 text-xs font-medium text-green-700">
                  Enabled
                </span>
              ) : (
                <span className="inline-flex items-center rounded-md bg-red-50 border border-red-200 px-2 py-0.5 text-xs font-medium text-red-700">
                  Disabled
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{connectionStatusText}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasUnsavedChanges ? (
            <Button size="sm" onClick={handleSave} disabled={isMutationPending || !canSave}>
              {upsert.isPending && <Loader2 className="size-3.5 animate-spin mr-1.5" />}
              Save
            </Button>
          ) : !isConnectionEnabled ? (
            <Button size="sm" onClick={handleEnable} disabled={isMutationPending}>
              {upsert.isPending && <Loader2 className="size-3.5 animate-spin mr-1.5" />}
              Enable connection
            </Button>
          ) : null}
        </div>
      </div>

      <Separator />

      {/* About */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-foreground mb-3">About</p>
        <p className="text-sm text-muted-foreground">{provider.description}</p>
      </div>

      {/* App launch URL (only when enabled) */}
      {isConnectionEnabled && (
        <div className="flex flex-col gap-1.5">
          <p className="text-sm font-medium">App launch URL</p>
          <p className="text-xs text-muted-foreground">
            After the EHR launch completes, Medblocks redirects the embedded browser to your app at this URL
            with a <code className="font-mono">?mb_launch=&lt;token&gt;</code> parameter.
          </p>
          <Input
            type="url"
            inputMode="url"
            placeholder="https://app.example.com/launch"
            value={appLaunchUrl}
            onChange={(e) => setAppLaunchUrl(e.target.value)}
            disabled={isMutationPending}
            className="max-w-xl"
          />
          {!isAppLaunchUrlValid && (
            <p className="text-xs text-destructive">Enter an absolute URL starting with http:// or https://</p>
          )}
          {upsert.isError && <p className="text-xs text-destructive">{upsert.error.message}</p>}
        </div>
      )}

      {/* CDS endpoint (only when enabled) */}
      {isConnectionEnabled && (
        <div className="flex flex-col gap-1.5">
          <p className="text-sm font-medium">CDS endpoint</p>
          <p className="text-xs text-muted-foreground">
            Your publicly accessible CDS Hooks service. Medblocks forwards CDS Hooks calls from the EHR to this URL.
          </p>
          <Input
            type="url"
            inputMode="url"
            placeholder="https://app.example.com/cds-services"
            value={cdsEndpoint}
            onChange={(e) => setCdsEndpoint(e.target.value)}
            disabled={isMutationPending}
            className="max-w-xl"
          />
          {!isCdsEndpointValid && (
            <p className="text-xs text-destructive">Enter an absolute URL starting with http:// or https://</p>
          )}
        </div>
      )}

      {/* CDS caching toggle (only when enabled) */}
      {isConnectionEnabled && (
        <div className="rounded-xl border bg-card px-5 py-4 flex items-start gap-4">
          <Switch checked={cdsCachingEnabled} onCheckedChange={setCdsCachingEnabled} disabled={isMutationPending} />
          <div>
            <p className="text-sm font-medium leading-tight">CDS caching</p>
            <p className="text-xs text-muted-foreground mt-1">
              Reuse the previous CDS card response when the patient's data hasn't changed, to speed up responses. Off by default.
            </p>
          </div>
        </div>
      )}

      {/* Launch configuration (read-only, to register in the EHR) */}
      <div className="flex flex-col gap-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-foreground">Launch configuration</p>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium">Client ID</p>
          <p className="text-xs text-muted-foreground">Share this with the organization to add to their Epic installation.</p>
          {savedRow?.client_id_in_use ? (
            <code className="mt-1 inline-flex w-fit items-center rounded-md bg-slate-100 border border-slate-200 px-2.5 py-1 font-mono text-xs text-slate-600 select-all">
              {savedRow.client_id_in_use}
            </code>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground italic">No client ID configured yet.</p>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium">Launch URL</p>
          <p className="text-xs text-muted-foreground">Ask the organization to configure this as the launch URL.</p>
          {launchUrl ? (
            <code className="mt-1 inline-flex w-fit items-center rounded-md bg-slate-100 border border-slate-200 px-2.5 py-1 font-mono text-xs text-slate-600 select-all">
              {launchUrl}
            </code>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground italic">Select an organization to view its launch URL.</p>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium">CDS Hooks URL</p>
          <p className="text-xs text-muted-foreground">Ask the organization to configure this as the CDS Hooks discovery endpoint.</p>
          {cdsHooksUrl ? (
            <code className="mt-1 inline-flex w-fit items-center rounded-md bg-slate-100 border border-slate-200 px-2.5 py-1 font-mono text-xs text-slate-600 select-all">
              {cdsHooksUrl}
            </code>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground italic">Select an organization to view its CDS Hooks URL.</p>
          )}
        </div>
      </div>

      {/* Card: credentials (only when enabled) */}
      {isConnectionEnabled && (
        <CustomCredentialsCard
          enabled={credentialMode === CREDENTIAL_MODE.OWN}
          onToggle={() => setCredentialMode(credentialMode === CREDENTIAL_MODE.OWN ? CREDENTIAL_MODE.MEDBLOCKS : CREDENTIAL_MODE.OWN)}
          clientId={customClientId}
          onClientIdChange={setCustomClientId}
          registrationUrl={provider.registrationUrl}
          providerLabel={provider.label}
          docsUrl={docsUrl}
          redirectUri={redirectUri}
          jwksUri={jwksUri}
          disabled={isMutationPending}
          error={upsert.isError ? upsert.error.message : null}
        />
      )}

      {/* Scopes */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-foreground mb-3">Scopes</p>
        <div className="flex flex-wrap gap-1.5">
          {provider.scopes.map((scope) => (
            <span
              key={scope}
              className="inline-flex items-center rounded-md bg-slate-100 border border-slate-200 px-2.5 py-1 font-mono text-xs text-slate-600 select-all"
            >
              {scope}
            </span>
          ))}
        </div>
      </div>

      {/* Danger zone (only when enabled) */}
      {isConnectionEnabled && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-destructive mb-3">Danger zone</div>
          <div className="border border-destructive/30 rounded-lg p-3">
            <p className="text-sm font-medium mb-1">Disable connection</p>
            <p className="text-xs text-muted-foreground mb-3">
              {provider.label} clinicians will no longer be able to launch your app. You can re-enable at any time.
            </p>
            <Button
              size="sm"
              variant="destructive"
              className="h-8 text-xs gap-1.5"
              onClick={handleDisable}
              disabled={isMutationPending}
            >
              {disable.isPending && <Loader2 className="size-3.5 animate-spin mr-1.5" />}
              Disable
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
